import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  Pin,
  PinOff,
  FileText,
  Calendar,
  User,
  BarChart2,
  Tag,
  Upload,
  X,
} from 'lucide-react';
import api from '../../services/api';
import RichTextEditor from '../../components/RichTextEditor';

const BlogManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    is_published: true,
    is_pinned: false,
    tags: ''
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/blog?include_unpublished=true');
      setPosts(response.data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      featured_image: '',
      is_published: true,
      is_pinned: false,
      tags: ''
    });
    setEditingPost(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      is_published: post.is_published ?? true,
      is_pinned: post.is_pinned || false,
      tags: (post.tags || []).join(', ')
    });
    setShowDialog(true);
  };

  const absoluteImageUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url; // external URL kept as-is
    const base = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    const MAX = 5 * 1024 * 1024;
    if (!/^image\/(jpeg|png|gif|webp)$/i.test(file.type)) {
      toast.error('Please pick a JPG, PNG, GIF, or WEBP image');
      return;
    }
    if (file.size > MAX) {
      toast.error('Image must be 5 MB or smaller');
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/blog/admin/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, featured_image: res.data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt || null,
        featured_image: formData.featured_image || null,
        is_published: formData.is_published,
        is_pinned: formData.is_pinned,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      if (editingPost) {
        await api.put(`/blog/${editingPost.id}`, payload);
        toast.success('Post updated successfully');
      } else {
        await api.post('/blog', payload);
        toast.success('Post created successfully');
      }

      setShowDialog(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    setDeleting(true);
    try {
      await api.delete(`/blog/${postId}`);
      toast.success('Post deleted successfully');
      setDeleteConfirm(null);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      await api.put(`/blog/${post.id}`, { is_published: !post.is_published });
      toast.success(post.is_published ? 'Post unpublished' : 'Post published');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const togglePin = async (post) => {
    try {
      await api.put(`/blog/${post.id}`, { is_pinned: !post.is_pinned });
      toast.success(post.is_pinned ? 'Post unpinned' : 'Post pinned to top');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Blog Management">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Blog Management">
      <div className="space-y-6" data-testid="blog-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Latest News & Updates</h1>
            <p className="text-slate-600">Manage blog posts visible to all users and guests</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="create-post-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{posts.length}</p>
                  <p className="text-sm text-slate-500">Total Posts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Eye className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{posts.filter(p => p.is_published).length}</p>
                  <p className="text-sm text-slate-500">Published</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Pin className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold">{posts.filter(p => p.is_pinned).length}</p>
                  <p className="text-sm text-slate-500">Pinned</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <BarChart2 className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{posts.reduce((sum, p) => sum + (p.view_count || 0), 0)}</p>
                  <p className="text-sm text-slate-500">Total Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Posts List */}
        {posts.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-lg font-medium text-slate-500">No blog posts yet</p>
              <p className="text-sm text-slate-400 mb-4">Create your first post to share news with your academy</p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <Card key={post.id} className={`${!post.is_published ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Featured Image */}
                    {post.featured_image && (
                      <div className="hidden sm:block w-32 h-24 flex-shrink-0">
                        <img 
                          src={absoluteImageUrl(post.featured_image)}
                          alt="" 
                          className="w-full h-full object-cover rounded-md"
                        />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {post.is_pinned && <Badge className="bg-amber-500"><Pin className="w-3 h-3 mr-1" />Pinned</Badge>}
                        {!post.is_published && <Badge variant="outline"><EyeOff className="w-3 h-3 mr-1" />Draft</Badge>}
                        {post.tags?.map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />{tag}
                          </Badge>
                        ))}
                      </div>
                      
                      <h3 className="text-lg font-semibold text-slate-900 truncate">{post.title}</h3>
                      
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                        {post.excerpt || post.content?.substring(0, 150)}
                      </p>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {post.author_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.view_count || 0} views
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-start gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePin(post)}
                        title={post.is_pinned ? 'Unpin' : 'Pin to top'}
                      >
                        {post.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePublish(post)}
                        title={post.is_published ? 'Unpublish' : 'Publish'}
                      >
                        {post.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(post)}
                        data-testid={`edit-post-${post.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(post)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter post title..."
                  className="mt-1"
                  data-testid="post-title-input"
                />
              </div>

              <div>
                <Label>Content *</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={formData.content}
                    onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                    placeholder="Write your post content..."
                    minHeight="250px"
                    maxHeight="400px"
                  />
                </div>
              </div>

              <div>
                <Label>Excerpt (optional)</Label>
                <Input
                  value={formData.excerpt}
                  onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="Short summary for preview..."
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Leave empty to auto-generate from content</p>
              </div>

              <div>
                <Label>Featured Image (optional)</Label>
                <div className="mt-1 space-y-2">
                  {formData.featured_image ? (
                    <div className="relative inline-block">
                      <img
                        src={absoluteImageUrl(formData.featured_image)}
                        alt="Featured"
                        className="h-40 w-auto max-w-full rounded-md border border-slate-200 object-cover"
                        data-testid="featured-image-preview"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, featured_image: '' }))}
                        className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-1 shadow hover:bg-red-50 hover:border-red-300"
                        title="Remove image"
                        data-testid="featured-image-remove"
                      >
                        <X className="h-3.5 w-3.5 text-red-600" />
                      </button>
                    </div>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-sm">
                      {uploadingImage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      <span>{formData.featured_image ? 'Replace image' : 'Upload image'}</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        className="hidden"
                        disabled={uploadingImage}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleImageUpload(f);
                          e.target.value = '';
                        }}
                        data-testid="featured-image-upload-input"
                      />
                    </label>
                    <span className="text-xs text-slate-400">or</span>
                    <Input
                      value={formData.featured_image}
                      onChange={(e) => setFormData((prev) => ({ ...prev, featured_image: e.target.value }))}
                      placeholder="Paste an image URL"
                      className="flex-1 min-w-[200px]"
                      data-testid="featured-image-url-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Upload a JPG/PNG/GIF/WEBP up to 5 MB, or paste a direct image URL.
                  </p>
                </div>
              </div>

              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="news, updates, cricket"
                  className="mt-1"
                />
              </div>

              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                  />
                  <Label>Published</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_pinned}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_pinned: checked }))}
                  />
                  <Label>Pin to Top</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-post-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Post</DialogTitle>
            </DialogHeader>
            <p className="py-4">
              Are you sure you want to delete "<strong>{deleteConfirm?.title}</strong>"? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={deleting}
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BlogManagement;
